import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDog } from '@fortawesome/pro-solid-svg-icons/faDog';
import { faCat } from '@fortawesome/pro-solid-svg-icons/faCat';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Angular source: apps/angular/src/js/ng/parent/add-pets-modal/*
// Lets a parent declare which pet types they have (dog / cat / other) as a
// tri-state: null (unset), true (yes), false (no). Save is enabled when at
// least one field is a truthy "yes".
export interface PetSelection {
  dog: boolean | null;
  cat: boolean | null;
  otherPet: boolean | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (pets: PetSelection) => Promise<void> | void;
}

type PetKey = 'dog' | 'cat' | 'otherPet';

interface PetRow {
  key: PetKey;
  label: string;
  suffix: string;
  icon: typeof faDog;
}

const PETS: PetRow[] = [
  { key: 'dog', label: 'Dogs', suffix: 'dog(s)', icon: faDog },
  { key: 'cat', label: 'Cats', suffix: 'cat(s)', icon: faCat },
  { key: 'otherPet', label: 'Other pets', suffix: 'other(s)', icon: faPaw },
];

// Cycle order mirrors Angular's cyclePetStatus: !parent[pet] flips between
// null-ish/false -> true -> false. We expose a third "unset" state so users
// can clear a selection; tapping a third time returns to null.
const cycle = (current: boolean | null): boolean | null => {
  if (current === null) return true;
  if (current === true) return false;
  return null;
};

export const AddPetsModal = ({
  isOpen,
  onClose,
  onSave,
}: Props): JSX.Element => {
  const [dog, setDog] = useState<boolean | null>(null);
  const [cat, setCat] = useState<boolean | null>(null);
  const [otherPet, setOtherPet] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const values: Record<PetKey, boolean | null> = { dog, cat, otherPet };
  const setters: Record<PetKey, (v: boolean | null) => void> = {
    dog: setDog,
    cat: setCat,
    otherPet: setOtherPet,
  };

  const canSave = !!(dog || cat || otherPet);

  const handleClose = (): void => {
    setDog(null);
    setCat(null);
    setOtherPet(null);
    onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      await onSave({ dog, cat, otherPet });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className='w-full max-w-lg'>
      <Button
        aria-label='Close'
        onClick={handleClose}
        className='absolute right-2 top-2 text-gray-400 hover:text-gray-500 transition-colors'
      >
        <FontAwesomeIcon icon={faXmark} className='w-5 h-5' />
      </Button>
      <h4 className='font-semibold mb-3 text-center'>What pets do you have?</h4>
      <p className='text-center text-neutral-600 mb-4'>
        <span className='hidden sm:inline'>Click</span>
        <span className='sm:hidden'>Tap</span> to select yes or no
      </p>
      <div className='grid grid-cols-3 gap-3 text-center mb-5'>
        {PETS.map(({ key, label, suffix, icon }) => {
          const value = values[key];
          return (
            <button
              key={key}
              type='button'
              title={label}
              onClick={(): void => setters[key](cycle(value))}
              className={`flex flex-col items-center p-3 rounded border transition-colors ${
                value === true
                  ? 'border-green-500 bg-green-50'
                  : value === false
                    ? 'border-red-500 bg-red-50'
                    : 'border-neutral-300'
              }`}
            >
              <FontAwesomeIcon icon={icon} className='text-3xl' aria-hidden='true' />
              <span className='text-sm mt-2'>
                {value != null && <strong>{value ? 'Yes' : 'No'} </strong>}
                {suffix}
              </span>
            </button>
          );
        })}
      </div>
      <div className='text-center'>
        <Button
          id='add-pets-modal-save'
          buttonType={ButtonType.primary}
          disabled={!canSave || submitting}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};
